<template>
  <div class="otb-app" :class="appClasses" :dir="dir">
    <otb-main-header
      ref="otbMain"
      :title="headerTitle"
      :goToHome="$t('header.return.model.link')"
      :modelName="modelName"
      :expandedMode="this.$route.meta.headerExpanded"
      :shouldShowLink="this.$route.meta.showBackLink"
      :shouldShowHeader="this.$route.meta.showHeader"
      :shouldShowSearch="this.$route.meta.showSearch"
      :shouldShowHeaderTooltip="this.$route.meta.showHeaderTooltip"
      :shouldShowReturn="this.$route.meta.showReturn"
      :vehicleImageLg="headerImageLg"
      :vehicleImageSm="headerImageSm"
      @toggleFiltersMenu="toggleFiltersMenu"
      @toggleDetailsModal="toggleDetailsModal"
    ></otb-main-header>
    <div class="otb-app-inner">
      <wb-grid class="otb-view-content">
        <wb-grid-row class="otb-view-content__row">
          <wb-grid-col class="otb-view-content__row__col" mq1="12">
            <main class="otb-main" :class="contentMarginTopClass">
              <router-view></router-view>
            </main>
          </wb-grid-col>
        </wb-grid-row>
      </wb-grid>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import MainHeader from './components/main-header/main-header.vue';
import { useDirection } from '@dh-io-otb/shared/src/composables/direction.composable';
import { useModel } from './composables/model.composable';
import { useRoutingApplication } from './composables/routing-application.composable';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';

import { SPECIFIC_ROUTE } from './models/constants';

import router from './router';
import { ROUTER } from './models/constants';

import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import BootstrapApplicationService from './services/bootstrap-application.service';
import BootstrapService from '@dh-io-otb/shared/src/services/bootstrap.service';
import Locale from '@dh-io-otb/shared/src/models/utilities/locale.model';

export default defineComponent({
  i18n,
  router,
  name: 'otb-app',
  components: {
    'otb-main-header': MainHeader,
  },
  props: {
    countryCode: {
      type: String,
      default: 'PT',
    },
    language: {
      type: String,
      default: 'pt',
    },
    isStandalone: {
      type: Boolean,
    },
  },
  setup(props) {
    const { dir } = useDirection();
    const { headerImageLg, headerImageSm, modelName } = useModel();
    const { isMobile, isSmallScreen } = useMediaQuery();
    const { metaParams, path, nextSpecificRoute } = useRoutingApplication();
    const otbMain = ref(null);
    const { state } = useStore();

    const overlayed = ref(false);

    const contentMarginTopClass = computed((): string => {
      const isRequestOnTablet = path?.value === ROUTER.REQUEST && isSmallScreen.value;
      const isHandheldDevice = isMobile.value || isRequestOnTablet;

      if (metaParams.value && metaParams.value.showReturn && !isHandheldDevice) return 'otb-content-header--return';

      if (metaParams.value.showHeader && metaParams.value.showSearch) {
        let headerClass =
          metaParams.value.headerExpanded && isHandheldDevice
            ? 'otb-content-header--expanded'
            : 'otb-content-header--collapsed';

        if (path?.value === ROUTER.REQUEST) headerClass += ` ${headerClass}--request`;
        return headerClass;
      }

      return 'otb-content--no-header';
    });

    const headerTitle = computed(() => {
      return i18n.t(metaParams.value.pageTitle ?? 'header.title');
    });

    const appClasses = computed(() => {
      return {
        'otb-app--overlayed': overlayed.value,
      };
    });

    const toggleFiltersMenu = (state: boolean) => {
      overlayed.value = state;
    };

    const toggleDetailsModal = (state: boolean) => {
      overlayed.value = state;
    };

    onMounted(async (): Promise<void> => {
      const countryLang = await BootstrapService.mountComponent(props);
      await BootstrapApplicationService.bootstrap(countryLang);
      watch(
        () => state.otbcFlow.errorMessage,
        (message) => {
          if (message) {
            nextSpecificRoute(SPECIFIC_ROUTE.MESSAGE);
          }
        },
      );
    });

    //TODO check if there is unnecessary duplication of requests
    const modelWatcher = watch(
      () => state.otbcFlow.model.modelSeries,
      () => {
        if (state.otbcFlow.model.modelSeries) {
          const locale: Locale = {
            countryCode: state.configuration.locale.countryCode || props.countryCode,
            language: state.configuration.locale.language || props.language,
          };
          BootstrapApplicationService.bootstrap(locale);
          modelWatcher();
        }
      },
    );

    return {
      appClasses,
      contentMarginTopClass,
      dir,
      headerImageLg,
      headerImageSm,
      headerTitle,
      modelName,
      otbMain,
      toggleFiltersMenu,
      toggleDetailsModal,
      isMobile,
    };
  },
});
</script>
<style src="./app.scss" lang="scss"></style>
